import Roles from 'src/security/roles';
import Plans from 'src/security/plans';
import Storage from 'src/security/storage';

const storage = Storage.values;
const roles = Roles.values;
const plans = Plans.values;

const allPlans = [
  plans.free,
  plans.starter,
  plans.basic,
  plans.standard,
  plans.plus,
  plans.pro,
  plans.custom,
];

const allowedCatalogPlans = [
  plans.starter,
  plans.basic,
  plans.standard,
  plans.plus,
  plans.pro,
  plans.custom,
];

const allowedImportPlans = [plans.basic, plans.standard, plans.plus, plans.pro];
const allowedWhiteLabelingPlans = [plans.plus, plans.pro];
// Every plan except free
const allowedPaidPlans = allPlans.filter(plan => plan !== plans.free);

const defaultAllowedRoles = [roles.superAdmin, roles.admin];

class Permissions {
  static get values() {
    return {
      superAdmin: {
        id: 'superAdmin',
        allowedRoles: [roles.superAdmin],
        allowedPlans: allPlans,
      },
      tenantEdit: {
        id: 'tenantEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      tenantDestroy: {
        id: 'tenantDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      whiteLabeling: {
        id: 'whiteLabeling',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedWhiteLabelingPlans,
      },
      qrCustomisation: {
        id: 'qrCustomisation',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedWhiteLabelingPlans,
      },
      planEdit: {
        id: 'planEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      planRead: {
        id: 'planRead',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      userEdit: {
        id: 'userEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      userDestroy: {
        id: 'userDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      userCreate: {
        id: 'userCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      userImport: {
        id: 'userImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      userRead: {
        id: 'userRead',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      userAutocomplete: {
        id: 'userAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      auditLogRead: {
        id: 'auditLogRead',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
      },
      settingsEdit: {
        id: 'settingsEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [
          storage.settingsBackgroundImages,
          storage.settingsLogos,
        ],
      },
      eLabelImport: {
        id: 'eLabelImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      eLabelCreate: {
        id: 'eLabelCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.eLabelPhotos],
      },
      eLabelEdit: {
        id: 'eLabelEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.eLabelPhotos],
      },
      eLabelDestroy: {
        id: 'eLabelDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.eLabelPhotos],
      },
      eLabelRead: {
        id: 'eLabelRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      eLabelAutocomplete: {
        id: 'eLabelAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      producerImport: {
        id: 'producerImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      producerCreate: {
        id: 'producerCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.producerLogo],
      },
      producerEdit: {
        id: 'producerEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.producerLogo],
      },
      producerDestroy: {
        id: 'producerDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.producerLogo],
      },
      producerRead: {
        id: 'producerRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      producerAutocomplete: {
        id: 'producerAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      nutritionInformationImport: {
        id: 'nutritionInformationImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      nutritionInformationCreate: {
        id: 'nutritionInformationCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      nutritionInformationEdit: {
        id: 'nutritionInformationEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      nutritionInformationDestroy: {
        id: 'nutritionInformationDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      nutritionInformationRead: {
        id: 'nutritionInformationRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      nutritionInformationAutocomplete: {
        id: 'nutritionInformationAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      imageImport: {
        id: 'imageImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      imageCreate: {
        id: 'imageCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.imageImage],
      },
      imageEdit: {
        id: 'imageEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.imageImage],
      },
      imageDestroy: {
        id: 'imageDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [storage.imageImage],
      },
      imageRead: {
        id: 'imageRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      imageAutocomplete: {
        id: 'imageAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      sustainabilityImport: {
        id: 'sustainabilityImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      sustainabilityCreate: {
        id: 'sustainabilityCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      sustainabilityEdit: {
        id: 'sustainabilityEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      sustainabilityDestroy: {
        id: 'sustainabilityDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      sustainabilityRead: {
        id: 'sustainabilityRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      sustainabilityAutocomplete: {
        id: 'sustainabilityAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      packagingCertificationImport: {
        id: 'packagingCertificationImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      packagingCertificationCreate: {
        id: 'packagingCertificationCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      packagingCertificationEdit: {
        id: 'packagingCertificationEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      packagingCertificationDestroy: {
        id: 'packagingCertificationDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      packagingCertificationRead: {
        id: 'packagingCertificationRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      packagingCertificationAutocomplete: {
        id: 'packagingCertificationAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      recyclingMaterialsImport: {
        id: 'recyclingMaterialsImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      recyclingMaterialsCreate: {
        id: 'recyclingMaterialsCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      recyclingMaterialsEdit: {
        id: 'recyclingMaterialsEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      recyclingMaterialsDestroy: {
        id: 'recyclingMaterialsDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      recyclingMaterialsRead: {
        id: 'recyclingMaterialsRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      recyclingMaterialsAutocomplete: {
        id: 'recyclingMaterialsAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      responsibleConsumptionImport: {
        id: 'responsibleConsumptionImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      responsibleConsumptionCreate: {
        id: 'responsibleConsumptionCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      responsibleConsumptionEdit: {
        id: 'responsibleConsumptionEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      responsibleConsumptionDestroy: {
        id: 'responsibleConsumptionDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      responsibleConsumptionRead: {
        id: 'responsibleConsumptionRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      responsibleConsumptionAutocomplete: {
        id: 'responsibleConsumptionAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      foodOperatorImport: {
        id: 'foodOperatorImport',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedImportPlans,
      },
      foodOperatorCreate: {
        id: 'foodOperatorCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      foodOperatorEdit: {
        id: 'foodOperatorEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      foodOperatorDestroy: {
        id: 'foodOperatorDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allPlans,
        allowedStorage: [],
      },
      foodOperatorRead: {
        id: 'foodOperatorRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      foodOperatorAutocomplete: {
        id: 'foodOperatorAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      catalogCreate: {
        id: 'catalogCreate',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedCatalogPlans,
        allowedStorage: [storage.catalogBranding],
      },
      catalogEdit: {
        id: 'catalogEdit',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedCatalogPlans,
        allowedStorage: [storage.catalogBranding],
      },
      catalogDestroy: {
        id: 'catalogDestroy',
        allowedRoles: defaultAllowedRoles,
        allowedPlans: allowedCatalogPlans,
        allowedStorage: [],
      },
      catalogRead: {
        id: 'catalogRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },
      analyticsRead: {
        id: 'analyticsRead',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allowedPaidPlans,
      },
      catalogAutocomplete: {
        id: 'catalogAutocomplete',
        allowedRoles: [...defaultAllowedRoles, roles.custom],
        allowedPlans: allPlans,
      },

      releaseNotificationRead: {
        id: 'releaseNotificationRead',
        allowedRoles: [roles.superAdmin, roles.admin, roles.custom],
        allowedPlans: allPlans,
      },
      releaseNotificationCreate: {
        id: 'releaseNotificationCreate',
        allowedRoles: [roles.superAdmin, roles.admin],
        allowedPlans: allPlans,
      },
      releaseNotificationEdit: {
        id: 'releaseNotificationEdit',
        allowedRoles: [roles.superAdmin, roles.admin],
        allowedPlans: allPlans,
      },
      releaseNotificationDestroy: {
        id: 'releaseNotificationDestroy',
        allowedRoles: [roles.superAdmin, roles.admin],
        allowedPlans: allPlans,
      },
    };
  }

  static get asArray() {
    return Object.keys(this.values).map(value => {
      return this.values[value];
    });
  }
}

export default Permissions;
